import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Archive, CaretUp, CaretDown, X, ArrowSquareOut, Star, Eye, EyeSlash, Bug, Lightbulb } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

const card = "bg-[#12151d] border border-[#232733] rounded-xl";
const inputCls = "w-full h-9 rounded-lg bg-[#0A0D14] border border-[#232733] px-3 text-xs text-white placeholder:text-[#4a5061] outline-none focus:border-[#FF6B35]";
const selectCls = "h-9 rounded-lg bg-[#0A0D14] border border-[#232733] px-2 text-xs text-white outline-none focus:border-[#FF6B35]";
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

const STATUS_LABEL = {
  planned: "Planned", in_development: "In Development", testing: "Testing", coming_soon: "Coming Soon",
  released: "Released", on_hold: "On Hold", submitted: "Submitted", under_review: "Under Review",
  accepted: "Accepted", in_progress: "In Progress", resolved: "Resolved", closed: "Closed",
  unable_to_reproduce: "Unable to Reproduce", not_planned: "Not Planned", considering: "Considering",
};
const STATUS_CLS = {
  planned: "bg-slate-500/15 text-slate-300", in_development: "bg-blue-500/15 text-blue-400",
  testing: "bg-purple-500/15 text-purple-400", coming_soon: "bg-amber-500/15 text-amber-400",
  released: "bg-emerald-500/15 text-emerald-400", on_hold: "bg-[#232733] text-[#8a90a0]",
  submitted: "bg-slate-500/15 text-slate-300", under_review: "bg-blue-500/15 text-blue-400",
  accepted: "bg-teal-500/15 text-teal-400", considering: "bg-teal-500/15 text-teal-400",
  in_progress: "bg-blue-500/15 text-blue-400", resolved: "bg-emerald-500/15 text-emerald-400",
  closed: "bg-[#232733] text-[#8a90a0]", unable_to_reproduce: "bg-amber-500/15 text-amber-400",
  not_planned: "bg-[#232733] text-[#8a90a0]",
};
const PRIORITY_CLS = { low: "text-slate-400", medium: "text-blue-400", high: "text-amber-400", critical: "text-red-400" };

const SBadge = ({ s }) => <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase whitespace-nowrap ${STATUS_CLS[s] || "bg-[#232733] text-[#8a90a0]"}`}>{STATUS_LABEL[s] || s || "—"}</span>;

const Overlay = ({ children, onClose, testid }) => (
  <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={onClose} data-testid={testid}>
    <div className={`${card} w-full max-w-2xl p-6`} onClick={(e) => e.stopPropagation()}>{children}</div>
  </div>
);

const EMPTY_ITEM = { title: "", short_description: "", detailed_description: "", category: "Platform", status: "planned", expected_release: "", release_date: "", release_notes: "", learn_more_url: "", featured: false, client_visible: true };

const RoadmapEditor = ({ item, categories, statuses, onClose, onSaved }) => {
  const [f, setF] = useState(item?.id ? { ...item } : { ...EMPTY_ITEM });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      if (f.id) await api.put(`/admin/roadmap/${f.id}`, f);
      else await api.post("/admin/roadmap", f);
      toast.success(f.id ? "Roadmap item updated" : "Roadmap item created");
      onSaved(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Overlay onClose={onClose} testid="roadmap-editor-overlay">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white">{f.id ? "Edit roadmap item" : "New roadmap item"}</h3>
        <button onClick={onClose} className="text-[#8a90a0] hover:text-white" data-testid="roadmap-editor-close"><X size={16} /></button>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Feature name *</label>
          <input value={f.title} onChange={(e) => set("title", e.target.value)} className={`${inputCls} mt-1`} data-testid="roadmap-editor-title" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Short description</label>
          <textarea value={f.short_description} onChange={(e) => set("short_description", e.target.value)} rows={2}
            className="mt-1 w-full rounded-lg bg-[#0A0D14] border border-[#232733] p-3 text-xs text-white outline-none focus:border-[#FF6B35] resize-none" data-testid="roadmap-editor-desc" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Category</label>
          <select value={f.category} onChange={(e) => set("category", e.target.value)} className={`${selectCls} mt-1 w-full`} data-testid="roadmap-editor-category">
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Status</label>
          <select value={f.status} onChange={(e) => set("status", e.target.value)} className={`${selectCls} mt-1 w-full`} data-testid="roadmap-editor-status">
            {statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Expected release</label>
          <input value={f.expected_release} onChange={(e) => set("expected_release", e.target.value)} placeholder="e.g. Q4 2026 · September 2026 · TBA" className={`${inputCls} mt-1`} data-testid="roadmap-editor-expected" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Release date (when released)</label>
          <input type="date" value={f.release_date || ""} onChange={(e) => set("release_date", e.target.value)} className={`${inputCls} mt-1`} data-testid="roadmap-editor-release-date" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Release notes</label>
          <textarea value={f.release_notes} onChange={(e) => set("release_notes", e.target.value)} rows={2}
            className="mt-1 w-full rounded-lg bg-[#0A0D14] border border-[#232733] p-3 text-xs text-white outline-none focus:border-[#FF6B35] resize-none" data-testid="roadmap-editor-notes" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Learn more URL</label>
          <input value={f.learn_more_url} onChange={(e) => set("learn_more_url", e.target.value)} placeholder="https://…" className={`${inputCls} mt-1`} data-testid="roadmap-editor-url" />
        </div>
        <label className="flex items-center gap-2 text-xs text-[#c9cfdb] font-bold cursor-pointer">
          <input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="roadmap-editor-featured" className="accent-[#FF6B35]" />
          Featured — show in "Currently Building"
        </label>
        <label className="flex items-center gap-2 text-xs text-[#c9cfdb] font-bold cursor-pointer">
          <input type="checkbox" checked={!!f.client_visible} onChange={(e) => set("client_visible", e.target.checked)} data-testid="roadmap-editor-visible" className="accent-[#FF6B35]" />
          Visible to clients
        </label>
      </div>
      <button onClick={save} disabled={busy} data-testid="roadmap-editor-save"
        className="mt-5 h-10 px-6 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white text-xs font-bold disabled:opacity-60">
        {busy ? "Saving…" : f.id ? "Save changes" : "Create item"}
      </button>
    </Overlay>
  );
};

export const RoadmapAdminTab = () => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/roadmap").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (id, direction) => {
    await api.post(`/admin/roadmap/${id}/move`, { direction }).catch(() => {});
    load();
  };
  const archive = async (it) => {
    if (!window.confirm(`Archive "${it.title}"? It will disappear from the client roadmap.`)) return;
    await api.delete(`/admin/roadmap/${it.id}`).catch(() => {});
    toast.success("Archived");
    load();
  };
  const quickToggle = async (it, field) => {
    await api.put(`/admin/roadmap/${it.id}`, { ...it, [field]: !it[field] }).catch(() => {});
    load();
  };

  if (!data) return <p className="text-sm text-[#8a90a0]">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="admin-roadmap-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white">Product Roadmap Management <span className="text-[#8a90a0] font-normal">· {data.items.length} items</span></h3>
        <button onClick={() => setEditing({})} data-testid="admin-roadmap-new-btn"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white text-xs font-bold">
          <Plus size={13} weight="bold" /> New item
        </button>
      </div>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[#232733] text-left text-[10px] font-bold uppercase tracking-wide text-[#8a90a0]">
              <th className="px-4 py-2.5">Order</th><th className="px-4 py-2.5">Feature</th><th className="px-4 py-2.5">Category</th>
              <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Expected / Released</th>
              <th className="px-4 py-2.5">Demand</th><th className="px-4 py-2.5">Flags</th><th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it, i) => (
              <tr key={it.id} className="border-b border-[#1a1e29] last:border-0 hover:bg-[#171b25]" data-testid={`admin-roadmap-row-${it.id}`}>
                <td className="px-4 py-2.5">
                  <span className="inline-flex flex-col">
                    <button onClick={() => move(it.id, "up")} disabled={i === 0} className="text-[#8a90a0] hover:text-white disabled:opacity-20" data-testid={`admin-roadmap-up-${it.id}`}><CaretUp size={12} weight="bold" /></button>
                    <button onClick={() => move(it.id, "down")} disabled={i === data.items.length - 1} className="text-[#8a90a0] hover:text-white disabled:opacity-20" data-testid={`admin-roadmap-down-${it.id}`}><CaretDown size={12} weight="bold" /></button>
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="font-bold text-white max-w-[220px] truncate">{it.title}</div>
                  <div className="text-[10px] text-[#8a90a0] max-w-[220px] truncate">{it.short_description}</div>
                </td>
                <td className="px-4 py-2.5 text-[#c9cfdb] whitespace-nowrap">{it.category}</td>
                <td className="px-4 py-2.5"><SBadge s={it.status} /></td>
                <td className="px-4 py-2.5 text-[#c9cfdb] whitespace-nowrap">{it.status === "released" ? fmtD(it.release_date) : it.expected_release || "—"}</td>
                <td className="px-4 py-2.5">
                  {it.demand > 0 ? <span className="px-2 py-0.5 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] font-bold" data-testid={`admin-roadmap-demand-${it.id}`}>{it.demand} account{it.demand > 1 ? "s" : ""}</span> : <span className="text-[#4a5061]">—</span>}
                </td>
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-2">
                    <button onClick={() => quickToggle(it, "featured")} title="Featured" data-testid={`admin-roadmap-star-${it.id}`}
                      className={it.featured ? "text-[#FF6B35]" : "text-[#4a5061] hover:text-[#8a90a0]"}><Star size={14} weight={it.featured ? "fill" : "regular"} /></button>
                    <button onClick={() => quickToggle(it, "client_visible")} title="Client visible" data-testid={`admin-roadmap-eye-${it.id}`}
                      className={it.client_visible ? "text-emerald-400" : "text-[#4a5061] hover:text-[#8a90a0]"}>{it.client_visible ? <Eye size={14} /> : <EyeSlash size={14} />}</button>
                  </span>
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <button onClick={() => setEditing(it)} className="text-[#8a90a0] hover:text-white p-1" data-testid={`admin-roadmap-edit-${it.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => archive(it)} className="text-[#8a90a0] hover:text-red-400 p-1" data-testid={`admin-roadmap-archive-${it.id}`}><Archive size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing !== null && (
        <RoadmapEditor item={editing.id ? editing : null} categories={data.categories} statuses={data.statuses}
          onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
};

const FeedbackDetail = ({ req, statuses, priorities, roadmapItems, onClose, onSaved }) => {
  const [status, setStatus] = useState(req.status);
  const [priority, setPriority] = useState(req.priority || "");
  const [assigned, setAssigned] = useState(req.assigned_to || "");
  const [linked, setLinked] = useState(req.linked_roadmap_item_id || "");
  const [response, setResponse] = useState(req.client_response || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/feedback/${req.id}`, {
        status, priority, assigned_to: assigned, client_response: response,
        linked_roadmap_item_id: linked, internal_note: note || null,
      });
      toast.success("Updated");
      onSaved(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} testid="admin-feedback-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[11px] font-bold bg-[#232733] text-[#c9cfdb] rounded px-2 py-0.5">{req.reference_number}</span>
            <SBadge s={req.status} />
            {req.priority && <span className={`text-[10px] font-bold uppercase ${PRIORITY_CLS[req.priority]}`}>{req.priority}</span>}
          </div>
          <h3 className="mt-1.5 text-sm font-bold text-white">{req.title}</h3>
          <p className="text-[11px] text-[#8a90a0]">{req.workspace_name || "—"} · {req.user_name} ({req.user_email}) · {fmtD(req.created_at)}</p>
        </div>
        <button onClick={onClose} className="text-[#8a90a0] hover:text-white shrink-0" data-testid="admin-feedback-close"><X size={16} /></button>
      </div>

      <div className="mt-4 bg-[#0A0D14] border border-[#232733] rounded-lg p-3 text-xs text-[#c9cfdb] whitespace-pre-wrap leading-relaxed">{req.description}</div>
      {req.business_value && (
        <div className="mt-2 text-xs text-[#8a90a0]"><b className="text-[#c9cfdb]">Why useful:</b> {req.business_value}</div>
      )}
      <div className="mt-2 flex items-center gap-4 flex-wrap text-[11px]">
        {req.page_url && <a href={req.page_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#6ea0ff] hover:underline" data-testid="admin-feedback-page-link">Reported page <ArrowSquareOut size={11} /></a>}
        {req.attachment_link && <a href={req.attachment_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-[#6ea0ff] hover:underline" data-testid="admin-feedback-attachment-link">Attachment <ArrowSquareOut size={11} /></a>}
        {req.context?.device && <span className="text-[#8a90a0]">{req.context.device}</span>}
        {req.context?.browser && <span className="text-[#4a5061] max-w-[300px] truncate" title={req.context.browser}>{req.context.browser}</span>}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${selectCls} mt-1 w-full`} data-testid="admin-feedback-status-select">
            {statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Priority</label>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className={`${selectCls} mt-1 w-full`} data-testid="admin-feedback-priority-select">
            <option value="">—</option>
            {priorities.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Assigned to</label>
          <input value={assigned} onChange={(e) => setAssigned(e.target.value)} placeholder="owner name/email" className={`${inputCls} mt-1`} data-testid="admin-feedback-assigned-input" />
        </div>
        {req.type === "FEATURE_REQUEST" && (
          <div>
            <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Link to roadmap</label>
            <select value={linked} onChange={(e) => setLinked(e.target.value)} className={`${selectCls} mt-1 w-full`} data-testid="admin-feedback-link-select">
              <option value="">Not linked</option>
              {roadmapItems.map((it) => <option key={it.id} value={it.id}>{it.title}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="mt-3">
        <label className="text-[10px] font-bold uppercase text-[#FF6B35]">Client-visible response</label>
        <textarea value={response} onChange={(e) => setResponse(e.target.value)} rows={2} placeholder="This message is shown to the client in My Requests…"
          className="mt-1 w-full rounded-lg bg-[#0A0D14] border border-[#FF6B35]/30 p-3 text-xs text-white outline-none focus:border-[#FF6B35] resize-none" data-testid="admin-feedback-response-input" />
      </div>

      <div className="mt-3">
        <label className="text-[10px] font-bold uppercase text-[#8a90a0]">Internal notes (never shown to clients)</label>
        {(req.internal_notes || []).length > 0 && (
          <div className="mt-1.5 space-y-1.5 max-h-32 overflow-y-auto">
            {req.internal_notes.map((n, i) => (
              <div key={i} className="bg-[#0A0D14] border border-[#232733] rounded-lg px-3 py-2 text-[11px] text-[#c9cfdb]">
                <span className="text-[#8a90a0] font-bold">{n.by} · {fmtD(n.at)}:</span> {n.text}
              </div>
            ))}
          </div>
        )}
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" className={`${inputCls} mt-1.5`} data-testid="admin-feedback-note-input" />
      </div>

      <button onClick={save} disabled={busy} data-testid="admin-feedback-save"
        className="mt-5 h-10 px-6 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white text-xs font-bold disabled:opacity-60">
        {busy ? "Saving…" : "Save changes"}
      </button>
    </Overlay>
  );
};

export const FeedbackAdminTab = ({ type }) => {
  const [data, setData] = useState(null);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [detail, setDetail] = useState(null);
  const isBug = type === "BUG";

  const load = useCallback(() => {
    const params = new URLSearchParams({ type });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (priority) params.set("priority", priority);
    api.get(`/admin/feedback?${params}`).then(({ data }) => setData(data)).catch(() => {});
  }, [type, q, status, priority]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isBug) api.get("/admin/roadmap").then(({ data }) => setRoadmapItems(data.items.filter((i) => i.status !== "released"))).catch(() => {});
  }, [isBug]);

  const statuses = data ? (isBug ? data.bug_statuses : data.feature_statuses) : [];
  return (
    <div className="space-y-4" data-testid={`admin-feedback-tab-${type.toLowerCase()}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          {isBug ? <Bug size={16} className="text-red-400" /> : <Lightbulb size={16} className="text-amber-400" />}
          {isBug ? "Bug Reports" : "Feature Requests"} <span className="text-[#8a90a0] font-normal">· {data?.items.length ?? "…"}</span>
        </h3>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, ref, tenant…" className={`${inputCls} w-52`} data-testid="admin-feedback-search" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls} data-testid="admin-feedback-status-filter">
            <option value="">All statuses</option>
            {statuses.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className={selectCls} data-testid="admin-feedback-priority-filter">
            <option value="">All priorities</option>
            {(data?.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      {!data ? <p className="text-sm text-[#8a90a0]">Loading…</p> : data.items.length === 0 ? (
        <p className="text-sm text-[#8a90a0]" data-testid="admin-feedback-empty">No {isBug ? "bug reports" : "feature requests"} match.</p>
      ) : (
        <div className={`${card} overflow-x-auto`}>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#232733] text-left text-[10px] font-bold uppercase tracking-wide text-[#8a90a0]">
                <th className="px-4 py-2.5">Reference</th><th className="px-4 py-2.5">Title</th><th className="px-4 py-2.5">Tenant</th>
                <th className="px-4 py-2.5">User</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Priority</th><th className="px-4 py-2.5">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id} onClick={() => setDetail(r)} data-testid={`admin-feedback-row-${r.reference_number}`}
                  className="border-b border-[#1a1e29] last:border-0 hover:bg-[#171b25] cursor-pointer">
                  <td className="px-4 py-2.5 font-mono font-bold text-white whitespace-nowrap">{r.reference_number}</td>
                  <td className="px-4 py-2.5 text-[#c9cfdb] max-w-[240px] truncate">{r.title}</td>
                  <td className="px-4 py-2.5 text-[#8a90a0] max-w-[140px] truncate">{r.workspace_name || "—"}</td>
                  <td className="px-4 py-2.5 text-[#8a90a0] max-w-[160px] truncate">{r.user_email}</td>
                  <td className="px-4 py-2.5"><SBadge s={r.status} /></td>
                  <td className="px-4 py-2.5"><span className={`font-bold uppercase text-[10px] ${PRIORITY_CLS[r.priority] || "text-[#4a5061]"}`}>{r.priority || "—"}</span></td>
                  <td className="px-4 py-2.5 text-[#8a90a0] whitespace-nowrap">{fmtD(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail && (
        <FeedbackDetail req={detail} statuses={statuses} priorities={data?.priorities || []} roadmapItems={roadmapItems}
          onClose={() => setDetail(null)} onSaved={load} />
      )}
    </div>
  );
};
